import { createContext, useCallback, useEffect, useState } from 'react';
import { addEdge, useEdgesState, useNodesState } from 'reactflow';
import Fetch from '../../../../../../core/util/core/Fetch';
import config from '../../../../../config/config';
import { useLocation } from 'react-router-dom';

export const OrganogramContext = createContext();

const path = config.endpoints.backend + 'organizationalcharts/save';

const pathAgencies = config.endpoints.backend + 'agencies/getall';

const pathAgencyDetails = config.endpoints.backend + 'agencies/getDetailsById';

export const OrganogramProvider = ({ children }) => {
    const [selectedNode, setSelectedNode] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);
    const [agencies, setAgencies] = useState([]);
    const [isLoadingDetails, setIsLoadingDetails] = useState(false);
    const [pdcEntityId, setPdcEntityId] = useState(null);
    const { pathname } = useLocation();


    const [nodeCount, setNodeCount] = useState(1);

    const isViewMode = pathname?.includes('view');


    const handleDeleteNode = useCallback(
        (nodeId) => {
            setNodes((nds) => nds.filter((node) => node.id !== nodeId));
            setEdges((eds) =>
                eds.filter(
                    (edge) => edge.source !== nodeId && edge.target !== nodeId
                )
            );
        },
        [setNodes, setEdges]
    );

    const clearMembers = useCallback(() => {
        setNodes((nds) =>
            nds.map((node) => ({
                ...node,
                data: {
                    ...node.data,
                    members: []
                }
            }))
        );
    }, [setNodes]);

    const fetchAgencyDetails = useCallback(
        async (agencyId) => {
            try {
                setIsLoadingDetails(true);
            if (!agencyId) return [];
            const agencyDetailsReturn = await Fetch.post(pathAgencyDetails, {
                id: agencyId
            });

                return agencyDetailsReturn;
            } finally {
                setIsLoadingDetails(false);
            }
        },
        [pathAgencyDetails]
    );

    const fetchAgencies = useCallback(async () => {
        if (!pdcEntityId) return;
        const agenciesReturn = await Fetch.post(pathAgencies, {
            filter: {
                pdc_entity_id: pdcEntityId
            }
        });

        setAgencies(agenciesReturn);
    }, [pdcEntityId]);

    useEffect(() => {
        fetchAgencies();
    }, [fetchAgencies, pdcEntityId]);

    const addNewNode = useCallback(() => {
        const newNodeId = `node-${Date.now()}`;

        const newNode = {
            id: newNodeId,
            type: 'orgNode',
            position: {
                x: 200 + (nodeCount % 3) * 250,
                y: 100 + Math.floor(nodeCount / 3) * 150
            },
            data: {
                title: '-',
                agency: '',
                description: '',
                members: [],
                onDelete: handleDeleteNode
            }
        };

        setNodes((nds) => [...nds, newNode]);
        setNodeCount((count) => count + 1);
    }, [setNodes, nodeCount, handleDeleteNode]);

    const onConnect = useCallback(
        (params) => {
            setEdges((eds) => {
                const reverseConnection = eds.find(
                    (edge) =>
                        edge.source === params.target &&
                        edge.target === params.source
                );

                if (reverseConnection) {
                    const newEdge = {
                        ...params,
                        type: 'default',
                        animated: true,
                        style: {
                            curvature: -0.2
                        },
                        markerEnd: {
                            type: 'arrowclosed',
                            width: 20,
                            height: 20
                        }
                    };

                    const updatedEdges = eds.map((edge) => {
                        if (edge.id === reverseConnection.id) {
                            return {
                                ...edge,
                                style: {
                                    ...edge.style,
                                    curvature: 0.2
                                },
                                markerEnd: {
                                    type: 'arrowclosed',
                                    width: 20,
                                    height: 20
                                }
                            };
                        }
                        return edge;
                    });

                    return [...updatedEdges, newEdge];
                } else {
                    const newEdge = {
                        ...params,
                        type: 'default',
                        animated: true,
                        style: {
                            curvature: 0
                        },
                        markerEnd: {
                            type: 'arrowclosed',
                            width: 20,
                            height: 20
                        }
                    };
                    return addEdge(newEdge, eds);
                }
            });
        },
        [setEdges]
    );

    useEffect(() => {
        setNodes((nds) =>
            nds.map((node) => ({
                ...node,
                data: {
                    ...node.data,
                    onDelete: handleDeleteNode
                }
            }))
        );
    }, [handleDeleteNode]);

    const openModal = (nodeData) => {
        setSelectedNode(nodeData);
        setIsModalOpen(true);
    };

    const handleSaveNode = (formData) => {
        const updatedNodes = nodes.map((node) => {
            if (node.id === selectedNode.id) {
                return {
                    ...node,
                    data: {
                        ...node.data,
                        ...formData
                    }
                };
            }
            return node;
        });

        setNodes(updatedNodes);

        setIsModalOpen(false);
    };

    const onDeleteNode = () => {
        if (selectedNode) {
            handleDeleteNode(selectedNode.id);
            setIsModalOpen(false);
        }
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedNode(null);
    };

    const loadNodesAndEdges = (newNodes, newEdges) => {
        setNodes(newNodes);
        setEdges(newEdges);
    };

    const handleSubmit = useCallback(
        async (preparedValues) => {
            const body = {
                ...preparedValues,
                graph: {
                    nodes,
                    edges
                }
            };

            await Fetch.put(path, body);
        },
        [edges, nodes]
    );

    return (
        <OrganogramContext.Provider
            value={{
                nodes,
                edges,
                onNodesChange,
                onEdgesChange,
                onConnect,
                addNewNode,
                openModal,
                handleSaveNode,
                onDeleteNode,
                closeModal,
                isModalOpen,
                selectedNode,
                loadNodesAndEdges,
                handleSubmit,
                setPdcEntityId,
                clearMembers,
                agencies,
                fetchAgencyDetails,
                isViewMode,
                isLoadingDetails
            }}
        >
            {children}
        </OrganogramContext.Provider>
    );
};
